import { Fragment, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi, type CsReportHour, type CsReportTotals } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { num } from '../../lib/format.js';

/**
 * CS 리포트 — 원본 /home/bananapartner/xadmin/cs_report.php.
 *
 * 시간별 집계(banana_custom.inhouse_cs_report)를 일자별로 접어 보여주고,
 * 일자를 누르면 그 날의 시간대별 내역이 펼쳐진다. 원본은 마우스를 올리면
 * 450px 짜리 레이어가 절대위치로 떠서 스크롤·모바일에서 못 쓰는 구조였는데,
 * 다른 xadm 화면들(방문자·주문·애드워즈)과 같은 '눌러서 그 자리에 펼치기'로
 * 바꿨다.
 *
 * 오른쪽 위 물류 현황(총/완료/송장)은 원본이 화면에서 직접 부르던 bizhost
 * 송장 API 다 - 이 앱은 이미 같은 값을 배송현황 카운터에서 쓰고 있어
 * 그 경로(/xadm/logistics/counter)를 그대로 재사용한다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(days: number) {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}
const hh = (h: number) => String(h).padStart(2, '0');

export function CsReportPage() {
  const [params, setParams] = useSearchParams();

  const month = params.get('month') ?? '';
  const from = params.get('from') ?? daysAgoISO(30);
  const to = params.get('to') ?? todayISO();
  const weekdayParam = params.get('weekday');
  const weekday = weekdayParam === null ? null : Number(weekdayParam);

  const [draft, setDraft] = useState({ from, to });
  const [openDate, setOpenDate] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['xadm-cs-report', month, from, to, weekday],
    queryFn: () => xadmApi.csReport(month ? { month, weekday } : { from, to, weekday }),
  });

  const hours = useQuery({
    queryKey: ['xadm-cs-report-hours', openDate],
    queryFn: () => xadmApi.csReportHours(openDate!),
    enabled: openDate !== null,
  });

  // 원본 우상단 '물류 현황' 박스. 배송현황 카운터와 같은 실시간 송장 API 다.
  const counter = useQuery({
    queryKey: ['xadm-logistics-counter'],
    queryFn: () => xadmApi.logisticsCounter(),
  });

  function apply(next: { weekday?: number | null; month?: string | null } = {}) {
    const q: Record<string, string> = {};
    const m = next.month === undefined ? month : (next.month ?? '');
    if (m) {
      q.month = m;
    } else {
      q.from = draft.from;
      q.to = draft.to;
    }
    const w = next.weekday === undefined ? weekday : next.weekday;
    if (w !== null) q.weekday = String(w);
    setParams(q, { replace: true });
    setOpenDate(null);
  }

  function reset() {
    setDraft({ from: daysAgoISO(30), to: todayISO() });
    setParams({}, { replace: true });
    setOpenDate(null);
  }

  // 연도별로 묶은 월 버튼 (원본 :134-153)
  const byYear = new Map<string, string[]>();
  for (const m of list.data?.months ?? []) {
    if (!byYear.has(m.year)) byYear.set(m.year, []);
    byYear.get(m.year)!.push(m.month);
  }

  return (
    <section className="page--wide">
      <header className="page-head">
        <div>
          <h1 className="page-title">CS 리포트</h1>
          <p className="page-desc">
            시간별 콜수·게시판 문의·전화주문과 그 시각의 출고 현황입니다. 일자를 누르면 시간대별로 펼쳐집니다.
          </p>
        </div>
        <div className="page-head__actions">{list.isFetching && <span className="page-head__busy">갱신 중…</span>}</div>
      </header>

      {/* 실시간 물류 현황 - 원본이 표 오른쪽 위에 두던 작은 박스 */}
      <div className="cs-logis" aria-label="물류 현황">
        <span className="cs-logis__title">물류 현황</span>
        {counter.isPending ? (
          <span className="cs-logis__loading">불러오는 중…</span>
        ) : counter.isError ? (
          <span className="cs-logis__loading">불러오지 못했습니다</span>
        ) : (
          <>
            <div className="cs-logis__item">
              <span>총</span>
              <strong>{num(counter.data.total)}</strong>
            </div>
            <div className="cs-logis__item">
              <span>완료</span>
              <strong className="td-ok">{num(counter.data.processed)}</strong>
            </div>
            <div className="cs-logis__item">
              <span>송장</span>
              <strong className="td-danger">{num(counter.data.notProcessed)}</strong>
            </div>
          </>
        )}
      </div>

      {list.data?.tableMissing && (
        <div className="cs-warn" role="status">
          <strong>집계 테이블이 아직 없습니다.</strong>
          <span>
            <code>banana_custom.inhouse_cs_report</code> 가 없어 표가 비어 있습니다.
            <code>migrations/004_inhouse_cs_report.sql</code> 을 DBA 가 적용하면 배치(cron-cs-report)가 매시
            쌓기 시작합니다.
          </span>
        </div>
      )}

      <div className="filters">
        <Field label="시작일">
          <input
            className="input"
            type="date"
            value={month ? (list.data?.from ?? draft.from) : draft.from}
            disabled={Boolean(month)}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          />
        </Field>
        <Field label="종료일">
          <input
            className="input"
            type="date"
            value={month ? (list.data?.to ?? draft.to) : draft.to}
            disabled={Boolean(month)}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
          />
        </Field>
        <div className="filters__buttons">
          <button type="button" className="btn btn--primary" onClick={() => apply({ month: null })}>
            검색
          </button>
          <button type="button" className="btn btn--ghost" onClick={reset}>
            초기화
          </button>
        </div>
      </div>

      {/* 요일 필터 - 원본은 일요일(0)을 고르면 '전체'로 되돌아가는 버그가 있었다. */}
      <div className="toolbar">
        <span className="join-weekbar__label">요일</span>
        {WEEKDAYS.map((label, w) => (
          <button
            key={w}
            type="button"
            className={weekday === w ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            aria-pressed={weekday === w}
            onClick={() => apply({ weekday: weekday === w ? null : w })}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className={weekday === null ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          aria-pressed={weekday === null}
          onClick={() => apply({ weekday: null })}
        >
          전체
        </button>
      </div>

      {byYear.size > 0 && (
        <div className="cs-months">
          {[...byYear.entries()].map(([year, months]) => (
            <div className="cs-months__row" key={year}>
              <span className="cs-months__year">{year}년</span>
              {months.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={month === m ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                  aria-pressed={month === m}
                  onClick={() => apply({ month: month === m ? null : m })}
                >
                  {Number(m.slice(-2))}월
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.days.length === 0 ? (
        <EmptyBox>이 구간에는 자료가 없습니다.</EmptyBox>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>일자</th>
                <th>요일</th>
                <th style={{ textAlign: 'right' }}>마지막 집계</th>
                <th style={{ textAlign: 'right' }}>콜수</th>
                <th style={{ textAlign: 'right' }}>게시판</th>
              </tr>
            </thead>
            <tbody>
              {list.data.days.map((d) => (
                <Fragment key={d.date}>
                  <tr
                    className="table__row--clickable"
                    onClick={() => setOpenDate(openDate === d.date ? null : d.date)}
                  >
                    <td>{d.date}</td>
                    <td className={d.weekday === 0 ? 'td-danger' : d.weekday === 6 ? 'adw-sat' : undefined}>
                      {WEEKDAYS[d.weekday] ?? '-'}
                    </td>
                    <td className="td-right td-muted">{hh(d.lastHour)}시</td>
                    <td className="td-right">{num(d.callCount)}</td>
                    <td className="td-right">{num(d.qnaCount)}</td>
                  </tr>

                  {openDate === d.date && (
                    <tr>
                      <td colSpan={5}>
                        {hours.isPending ? (
                          <Spinner />
                        ) : hours.isError ? (
                          <ErrorBox error={hours.error} />
                        ) : (
                          <HourTable
                            date={d.date}
                            weekday={d.weekday}
                            rows={hours.data?.hours ?? []}
                            totals={hours.data?.totals}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * 하루의 시간대별 내역. 원본과 같이 오전(11시)·마감(평일 18시, 토 15시)에서
 * 그때까지의 소계를 한 줄 끼우고, 맨 아래에 하루 합계를 둔다.
 *
 * 출고 세 칸은 건수가 아니라 그 시각의 현황이라 합계 줄에서도 '마지막 값'이다
 * (원본과 같다) - 표에서 헷갈리지 않게 합계 줄에 그 뜻을 적어둔다.
 */
function HourTable({
  date, weekday, rows, totals,
}: {
  date: string; weekday: number; rows: CsReportHour[]; totals?: CsReportTotals;
}) {
  if (rows.length === 0) return <EmptyBox>{date} 자료가 없습니다.</EmptyBox>;

  return (
    <table className="table table--nested">
      <caption className="cs-detail__caption">
        {date} ({WEEKDAYS[weekday] ?? '-'})요일
      </caption>
      <thead>
        <tr>
          <th>시간대</th>
          <th style={{ textAlign: 'right' }}>콜수</th>
          <th style={{ textAlign: 'right' }}>게시판</th>
          <th style={{ textAlign: 'right' }}>전화주문</th>
          <th style={{ textAlign: 'right' }}>출고전체</th>
          <th style={{ textAlign: 'right' }}>출고완료</th>
          <th style={{ textAlign: 'right' }}>출고대기</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((h) => (
          <Fragment key={h.hour}>
            <tr>
              <td>{hh(h.hour)}시</td>
              <td className="td-right">{num(h.callCount)}</td>
              <td className="td-right">{num(h.qnaCount)}</td>
              <td className="td-right">{num(h.adminOrder)}</td>
              <td className="td-right td-muted">{num(h.devTotal)}</td>
              <td className="td-right td-muted">{num(h.devIng)}</td>
              <td className="td-right td-muted">{num(h.devStay)}</td>
            </tr>
            {h.subtotal && (
              <tr className="cs-subtotal">
                <td>{h.subtotal.label} 합계</td>
                <td className="td-right">{num(h.subtotal.callCount)}</td>
                <td className="td-right">{num(h.subtotal.qnaCount)}</td>
                <td className="td-right">{num(h.subtotal.adminOrder)}</td>
                <td className="td-right">{num(h.subtotal.devTotal)}</td>
                <td className="td-right">{num(h.subtotal.devIng)}</td>
                <td className="td-right">{num(h.subtotal.devStay)}</td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
      {totals && (
        <tfoot>
          <tr className="row--total">
            <td title="출고 세 칸은 건수가 아니라 그 시각의 현황이라 마지막 값입니다">합계</td>
            <td className="td-right">{num(totals.callCount)}</td>
            <td className="td-right">{num(totals.qnaCount)}</td>
            <td className="td-right">{num(totals.adminOrder)}</td>
            <td className="td-right">{num(totals.devTotal)}</td>
            <td className="td-right">{num(totals.devIng)}</td>
            <td className="td-right">{num(totals.devStay)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}
